import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useInterval } from '../hooks/useInterval';
import { getSelectedRows } from '../utils/getSelectedRows';
import { useColumnOrder } from './useColumnOrder';
import { useColumns } from './useColumns';
import { useRangeLoader } from './useRangeLoader';
import { useRowFilterCondition } from './useRowFilterCondition';
import { useSelection } from './useSelection';
import { useSorting } from './useSorting';
const useColumnsApiCall = (getColumnsDescriptionApiCall) => {
    const { data: columns, refetch: reloadColumns, error: columnsError, } = useQuery({
        queryKey: ['tableColumns'],
        queryFn: getColumnsDescriptionApiCall,
        retry: false,
    });
    return {
        columns: columns || [],
        reloadColumns,
        columnsError,
    };
};
/**
 * @param options.tableKey - key to store column order in local storage
 * @param options.autoRefreshDelay - delay in ms between auto refreshes, if undefiend auto refresh is disabled
 */
export const useSmartTable = (options) => {
    const { tableKey, api, setQueryParam, getQueryParam, rowsSelectEnabled, fixedFilterCondition, defaultHiddenColumns, defaultSorting, autoRefreshDelay, onInvalidate, prepareNewRow, newRowFieldOverrides, renderExtraContent, } = options;
    const { rowsApiCall, columnsDefinitionApiCall, deleteRows } = api;
    const { isDefaultSorting, sorting, toggleSorting, clearSorting, removeColumnSorting, setColumnSorting, setAsOnlySorting, } = useSorting(setQueryParam, getQueryParam, defaultSorting);
    const [rowFilterCondition, setRowFilterCondition] = useRowFilterCondition(setQueryParam, getQueryParam, fixedFilterCondition);
    const { columns, reloadColumns, columnsError } = useColumnsApiCall(columnsDefinitionApiCall);
    const { columnOrder, setColumnOrder } = useColumnOrder(tableKey, columns);
    const { allColumns, visibleColumns, isColumnVisible, setAllColumnsHidden, setAllColumnsVisible, setColumnVisibility, toggleColumnVisibility, } = useColumns({ tableKey, columns, order: columnOrder, defaultHiddenColumns });
    const { loadRange, rows, totalRows, lastUpdated, reset: resetRows, softReset: softResetRows, isLoading: rowsLoading, } = useRangeLoader(rowsApiCall, rowFilterCondition, sorting);
    const { deselectAll, selectAll, setSelected, isSelected, allSelected, selectionCount, toggleSelected } = useSelection(rows, totalRows, [rowFilterCondition, allColumns]);
    const selectedRows = useMemo(() => getSelectedRows(rows, isSelected), [rows, isSelected]);
    const invalidateTable = useCallback(() => {
        reloadColumns();
        resetRows();
        onInvalidate?.();
    }, [reloadColumns, resetRows, onInvalidate]);
    useInterval(() => {
        reloadColumns();
        resetRows();
        onInvalidate?.();
    }, autoRefreshDelay ?? null, {
        skipFirstRun: true,
    });
    return useMemo(() => ({
        tableKey,
        rows,
        rowsLoading,
        totalRows,
        lastUpdated,
        selectedRows,
        loadRange,
        deleteRows,
        allColumns,
        visibleColumns,
        isColumnVisible,
        setColumnVisibility,
        toggleColumnVisibility,
        setAllColumnsHidden,
        setAllColumnsVisible,
        sorting,
        setColumnSorting,
        removeColumnSorting,
        setAsOnlySorting,
        toggleSorting,
        clearSorting,
        isDefaultSorting,
        rowFilterCondition,
        setRowFilterCondition,
        columnOrder,
        setColumnOrder,
        rowsSelectEnabled,
        deselectAll,
        selectAll,
        setSelected,
        isSelected,
        allSelected,
        selectionCount,
        toggleSelected,
        resetRows,
        softResetRows,
        reloadColumns,
        getQueryParam,
        columnsError,
        invalidateTable,
        api,
        prepareNewRow,
        newRowFieldOverrides,
        renderExtraContent,
    }), [
        tableKey,
        rows,
        rowsLoading,
        totalRows,
        lastUpdated,
        selectedRows,
        setColumnSorting,
        removeColumnSorting,
        setAsOnlySorting,
        loadRange,
        deleteRows,
        allColumns,
        visibleColumns,
        isColumnVisible,
        setColumnVisibility,
        toggleColumnVisibility,
        setAllColumnsHidden,
        setAllColumnsVisible,
        sorting,
        toggleSorting,
        clearSorting,
        isDefaultSorting,
        rowFilterCondition,
        setRowFilterCondition,
        columnOrder,
        setColumnOrder,
        rowsSelectEnabled,
        deselectAll,
        selectAll,
        setSelected,
        isSelected,
        allSelected,
        selectionCount,
        toggleSelected,
        resetRows,
        softResetRows,
        reloadColumns,
        getQueryParam,
        columnsError,
        invalidateTable,
        api,
        prepareNewRow,
        newRowFieldOverrides,
        renderExtraContent,
    ]);
};
//# sourceMappingURL=useSmartTable.js.map